import React from 'react'
import { TextInput } from '@/components/atoms'
import { preventInvalidNumericInput } from '@/utils/inputValidations'
import { REWARD_TYPES } from '@/utils/constants/rewardsConstants'
import styles from './InventoryFieldsRow.module.scss'

/**
 * InventoryFieldsRow component - Reusable component for quota, leftover inventory, and limit per store fields
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing quota, leftoverInventory, limitPerStore
 * @param {boolean} props.isQuotaDisabled - Whether quota field should be disabled
 * @param {Function} props.handleQuotaChange - Function to handle quota field changes
 * @param {Function} props.handleLeftoverInventoryChange - Function to handle leftover inventory field changes
 * @param {Function} props.handleLimitPerStoreChange - Function to handle limit per store field changes
 * @param {Function} props.handleMaxQuantityPerOrderChange - Function to handle max quantity per order field changes
 * @param {string} props.quotaValidationError - Validation error message for quota field
 * @param {string} props.limitValidationError - Validation error message for limit per store field
 * @param {string} props.maxQuantityPerOrderValidationError - Validation error message for max quantity per order field
 * @returns {JSX.Element} The rendered InventoryFieldsRow component
 */
const InventoryFieldsRow = ({
  formData,
  isQuotaDisabled,
  handleQuotaChange,
  handleLeftoverInventoryChange,
  handleLimitPerStoreChange,
  handleMaxQuantityPerOrderChange,
  quotaValidationError,
  limitValidationError,
  maxQuantityPerOrderValidationError,
}) => {
  const isPepVoucherDiscount = formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT
  const renderTextInput = ({
    id,
    label,
    value,
    onChange,
    placeholderText,
    className = '',
    isDark = true,
    type = 'text',
    disabled = false,
    handleKeyDown = preventInvalidNumericInput,
    error,
  }) => (
    <div className="w-full md:w-[262px]">
      <TextInput
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        placeholderText={placeholderText}
        className={`w-full text-sm placeholder:text-sm ${className}`}
        isDark={isDark}
        type={type}
        disabled={disabled}
        handleKeyDown={handleKeyDown}
        error={error}
      />
    </div>
  )

  return (
    <div className="flex flex-col items-start gap-6 md:flex-row">
      {renderTextInput({
        id: 'quota',
        label: 'Quota*',
        value: formData?.quota || '',
        onChange: handleQuotaChange,
        placeholderText: 'Add Quota',
        className: styles.inventoryFieldsRow__quota,
        disabled: isQuotaDisabled,
        error: quotaValidationError,
      })}

      {renderTextInput({
        id: 'add-inventory',
        label: 'Leftover Inventory',
        value: formData?.leftoverInventory || '',
        onChange: handleLeftoverInventoryChange,
        placeholderText: 'Leftover Inventory',
        className: styles.inventoryFieldsRow__leftoverInventory,
        disabled: true,
      })}

      {renderTextInput({
        id: 'limit-per-store',
        label: 'Limit per Store',
        value: formData?.limitPerStore || '',
        onChange: handleLimitPerStoreChange,
        placeholderText: 'Limit Account',
        className: styles.inventoryFieldsRow__limitPerStore,
        error: limitValidationError,
      })}

      {isPepVoucherDiscount && renderTextInput({
        id: 'max-quantity-per-order',
        label: 'Max Quantity per Order*',
        value: formData?.maxQuantityPerOrder || '',
        onChange: handleMaxQuantityPerOrderChange,
        placeholderText: 'Max Quantity per Order',
        className: styles.inventoryFieldsRow__limitPerStore,
        error: maxQuantityPerOrderValidationError,
      })}
    </div>
  )
}

export default InventoryFieldsRow
